import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * An add from inside an open quick add dialog waits for that dialog to close. A
   * successful add closes it (assets/quick-add.js), and the close animates before clearing
   * the body scroll lock and restoring the scroll position (assets/dialog.js). Opening
   * during that window leaves the page unlocked behind the drawer, and records `scrollY` as
   * 0 while the body is still fixed, so closing the drawer jumps the page to the top.
   * The native `close` event fires after all of that. A failed add leaves the quick add
   * open, so it opens nothing.
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    const quickAdd = event.target instanceof Element ? event.target.closest('quick-add-dialog') : null;
    const quickAddDialog = quickAdd?.querySelector('dialog[open]');

    if (quickAddDialog && !this.contains(quickAddDialog)) {
      if (/** @type {any} */ (event).detail?.data?.didError) return;
      quickAddDialog.addEventListener('close', () => this.showDialog(), { once: true });
      return;
    }

    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  get skipCloseAnimationReflow() {
    return true;
  }

  /**
   * Defers body scroll lock to the next animation frame.
   *
   * The base `showDialog` rAF does `applyBodyScrollLock` then `showModal()`.
   * For the cart drawer that stacks two full-layout operations — body
   * `position:fixed` (page relayout) then `showModal()` (full cart DOM layout)
   * — in a single frame, which dominates INP on open (~600ms on a full cart).
   *
   * By scheduling the lock one frame later, `showModal()` runs alone in the
   * INP-critical frame. The body lock lands in the subsequent frame at no
   * perceptible cost.
   *
   * Guard: if the dialog is already closed by the time the rAF fires (e.g.
   * near-instant open→close), skip the lock to avoid incorrectly re-locking
   * the page after `closeDialog` has already cleared the body styles.
   *
   * @param {number} scrollY
   * @override
   */
  applyBodyScrollLock(scrollY) {
    requestAnimationFrame(() => {
      if (!this.refs.dialog?.open) return;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    });
  }

  // Arrow field, not a method: it is passed as a document listener, where a method's
  // `this` would be `document` and the private call would throw.
  #updateStickyState = () => {
    requestAnimationFrame(() => this.#applyStickyState());
  };

  #applyStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const isCompactLayout =
      this.dataset.drawerLayout === 'compact' || dialog.classList.contains('cart-drawer--layout-compact');

    if (isCompactLayout && isMobileBreakpoint()) {
      dialog.setAttribute('cart-summary-sticky', 'true');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
